import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  active: { c: "bg-green-500/20 text-green-400", t: "Active" },
  paused: { c: "bg-yellow-500/20 text-yellow-400", t: "Paused" },
  completed: { c: "bg-blue-500/20 text-blue-400", t: "Completed" },
  cancelled: { c: "bg-red-500/20 text-red-400", t: "Cancelled" },
};

const P2PMyAdsPage = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const r = await api.get("/p2p/ads/my");
      setAds(r.data.ads || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line
  }, []);

  const [pendingCancelAdId, setPendingCancelAdId] = useState(null);

  const handleCancel = async (adId) => {
    if (pendingCancelAdId !== adId) {
      setPendingCancelAdId(adId);
      setTimeout(() => setPendingCancelAdId((cur) => (cur === adId ? null : cur)), 4000);
      toast.info("Tap once more to CONFIRM cancel (USDT will return to your wallet)");
      return;
    }
    setPendingCancelAdId(null);
    setBusy(true);
    try {
      const r = await api.post(`/p2p/ads/${adId}/cancel`);
      toast.success(`Ad cancelled. Refunded ${r.data.refunded_usdt} USDT`);
      fetchAds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
        <button onClick={() => navigate("/p2p")} className="p-1 mr-2" data-testid="my-ads-back-btn">
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold flex-1">My Ads</h1>
        <button
          onClick={() => navigate("/p2p/post?source=outside")}
          className="bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
          data-testid="post-new-ad-btn"
        >
          <Plus size={14} /> New
        </button>
      </div>

      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">No ads yet. Post your first one!</div>
        ) : (
          ads.map((ad) => {
            const s = STATUS_MAP[ad.status] || { c: "bg-muted", t: ad.status };
            return (
              <div key={ad.id} className="bg-card border border-border rounded-xl p-3 space-y-2" data-testid={`my-ad-${ad.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">#{ad.id.slice(0, 8)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.c}`}>{s.t}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">{ad.total_amount_usdt} USDT @ {ad.price_per_usdt} {ad.fiat}</span>
                  <span className="text-primary font-bold">
                    {(ad.available_amount_usdt || 0).toFixed(2)} left
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {ad.completed_trades || 0} completed • Source: {ad.source_wallet === "outside" ? "Main USDT" : "Game"} • Locked: {(ad.locked_amount_usdt || 0).toFixed(2)} USDT
                </div>
                {ad.status === "active" && (
                  <button
                    onClick={() => handleCancel(ad.id)}
                    disabled={busy}
                    className={`w-full py-1.5 rounded-lg text-xs flex items-center justify-center gap-1 transition ${
                      pendingCancelAdId === ad.id
                        ? "bg-red-600 text-white font-bold animate-pulse"
                        : "border border-red-500/50 text-red-400"
                    }`}
                    data-testid={`cancel-ad-${ad.id}`}
                  >
                    {pendingCancelAdId === ad.id ? (
                      "⚠️ Tap Again to Confirm Cancel"
                    ) : (
                      <><Trash2 size={12} /> Cancel & Refund</>
                    )}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default P2PMyAdsPage;
